import React from 'react';
import { token, getGlobalTheme } from '@atlaskit/tokens';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { IllustrationWrapper } from '../../../../common/ui/illustration/illustration-wrapper';
import {
	transformProjectKeyText,
	truncateText,
} from '../../../../common/ui/illustration/issue-key-text/utils';
import messages from './messages';

export type Props = {
	isTmp: boolean;
	projectName: string;
	projectKey: string;
	teamManagedIllustrationFooter: MessageDescriptor;
	companyManagedIllustrationFooter: MessageDescriptor;
};

export const IllustrationJWM = ({
	projectName,
	projectKey,
	isTmp,
	teamManagedIllustrationFooter,
	companyManagedIllustrationFooter,
}: Props) => {
	const { formatMessage } = useIntl();
	const { colorMode } = getGlobalTheme();
	return (
		<IllustrationWrapper data-testId="project-create-importer-upload-flow.project-create-screen.illustration.jwm.wrapper">
			<svg
				width="469"
				height="327"
				viewBox="0 0 469 327"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				role="img"
				aria-label={formatMessage(messages.illustrationLabel)}
			>
				<title>{formatMessage(messages.illustrationLabel)}</title>
				<path
					d="M231.768 9.98332C275.273 12.6401 330.466 15.1635 366.484 33.4939C402.868 52.0105 406.389 50.8482 423.647 81.0084C442.896 114.649 482.028 174.233 464.666 208.437C447.246 242.756 382.018 243.524 341.213 264.155C302.326 283.815 278.659 323.507 231.768 326.188C184.451 328.893 144.593 301.044 109.367 277.408C77.2776 255.876 58.5204 253.739 38.922 225.099C14.9905 190.126 -7.34554 147.23 2.29851 108.654C12.1046 69.4304 40.0039 22.3787 89.0874 6.23079C133.696 -8.44509 183.148 7.01424 231.768 9.98332Z"
					fill="url(#paint0_linear_4253_17324)"
				/>
				<g filter="url(#filter0_d_4253_17324)">
					<rect
						x="22"
						y="14"
						width="410.164"
						height="268.961"
						rx="5.47227"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="145.613"
						y="32.4707"
						width="81.9962"
						height="8.2084"
						rx="4.1042"
						fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
					/>
					<path
						d="M21.9991 19.0894C21.9991 16.2786 24.2778 14 27.0886 14H132.945V282.961H27.0886C24.2778 282.961 21.9991 280.682 21.9991 277.871V19.0894Z"
						fill={token('elevation.surface.sunken', '#F7F8F9')}
					/>
					<path
						d="M21.9991 14H132.945H21.9991ZM132.945 282.961H21.9991H132.945ZM21.9991 282.961V14V282.961ZM133.631 14V282.961H132.26V14H133.631Z"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="35.4757"
						y="30.4521"
						width="13.5718"
						height="13.5718"
						rx="3.39295"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<text
						fill={token('color.text.subtle', '#44546F')}
						fontSize="11"
						letterSpacing="0px"
						x="54.5199"
						y="40.1284"
					>
						{truncateText(projectName, 10)}
					</text>
					<rect
						x="36.0421"
						y="69.2129"
						width="81.9962"
						height="8.2084"
						rx="4.1042"
						fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
					/>
					<rect
						x="35.4761"
						y="52.249"
						width="81.9962"
						height="8.2084"
						rx="4.1042"
						fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
					/>
					<rect
						x="36.0421"
						y="86.1787"
						width="81.9962"
						height="8.2084"
						rx="4.1042"
						fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
					/>
					<text
						fill={token('color.text.subtlest', '#626F86')}
						fontSize="6.84033"
						letterSpacing="0px"
						x="35.4758"
						y="268.924"
					>
						{isTmp
							? formatMessage(teamManagedIllustrationFooter)
							: formatMessage(companyManagedIllustrationFooter)}
					</text>
					<path
						d="M423.76 76.0552H146.394V78.0552H423.76V76.0552Z"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="146.394"
						y="50.7646"
						width="31.5487"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="156.911"
						y="61"
						width="10.5162"
						height="5.25812"
						rx="2.62906"
						fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
					/>
					<rect
						x="177.943"
						y="50.7646"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="179.258"
						y="50.7646"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="187.145"
						y="61"
						width="42.0649"
						height="5.25812"
						rx="2.62906"
						fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
					/>
					<rect
						x="237.097"
						y="50.7646"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="238.411"
						y="50.7646"
						width="98.5897"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="247.613"
						y="61"
						width="78.8717"
						height="5.25812"
						rx="2.62906"
						fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
					/>
					<rect
						x="337.001"
						y="50.7646"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="338.316"
						y="50.7646"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="346.203"
						y="61"
						width="42.0649"
						height="5.25812"
						rx="2.62906"
						fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
					/>
					<rect
						x="396.155"
						y="50.7646"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="397.469"
						y="50.7646"
						width="26.2906"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="402.728"
						y="61"
						width="15.7743"
						height="5.25812"
						rx="2.62906"
						fill={token('color.background.accent.gray.subtler', '#DCDFE4')}
					/>
					<path
						d="M423.76 103.661H146.394V105.661H423.76V103.661Z"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="146.394"
						y="78.3701"
						width="31.5487"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="156.911"
						y="86.2578"
						width="10.5162"
						height="10.5162"
						rx="2.62906"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<rect
						x="177.943"
						y="78.3701"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="179.258"
						y="78.3701"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="237.097"
						y="78.3701"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="238.411"
						y="78.3701"
						width="98.5897"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="337.001"
						y="78.3701"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="338.316"
						y="78.3701"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="346.203"
						y="86.2578"
						width="42.0649"
						height="10.5162"
						rx="1.97179"
						fill={token('color.background.accent.gray.subtle', '#8590A2')}
					/>
					<rect
						x="396.155"
						y="78.3701"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="397.469"
						y="78.3701"
						width="26.2906"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="404.699"
						y="84.9434"
						width="13.1453"
						height="13.1453"
						rx="6.57264"
						fill={token('color.background.accent.red.subtle', '#F87168')}
					/>
					<path
						d="M423.76 131.266H146.394V133.266H423.76V131.266Z"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="146.394"
						y="105.976"
						width="31.5487"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="156.911"
						y="113.862"
						width="10.5162"
						height="10.5162"
						rx="2.62906"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<rect
						x="177.943"
						y="105.976"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="179.258"
						y="105.976"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="237.097"
						y="105.976"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="238.411"
						y="105.976"
						width="98.5897"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="337.001"
						y="105.976"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="338.316"
						y="105.976"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="346.203"
						y="113.862"
						width="42.0649"
						height="10.5162"
						rx="1.97179"
						fill={token('color.background.accent.gray.subtle', '#8590A2')}
					/>
					<rect
						x="396.155"
						y="105.976"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="397.469"
						y="105.976"
						width="26.2906"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="404.699"
						y="112.549"
						width="13.1453"
						height="13.1453"
						rx="6.57264"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<path
						d="M423.76 158.871H146.394V160.871H423.76V158.871Z"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="146.394"
						y="133.58"
						width="31.5487"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="156.911"
						y="141.467"
						width="10.5162"
						height="10.5162"
						rx="2.62906"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<rect
						x="177.943"
						y="133.58"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="179.258"
						y="133.58"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="237.097"
						y="133.58"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="238.411"
						y="133.58"
						width="98.5897"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="337.001"
						y="133.58"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="338.316"
						y="133.58"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="346.203"
						y="141.467"
						width="42.0649"
						height="10.5162"
						rx="1.97179"
						fill={token('color.background.accent.blue.subtler', 'CCE0FF')}
					/>
					<rect
						x="396.155"
						y="133.58"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="397.469"
						y="133.58"
						width="26.2906"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="404.699"
						y="140.152"
						width="13.1453"
						height="13.1453"
						rx="6.57264"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<path
						d="M423.76 186.476H146.394V188.476H423.76V186.476Z"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="146.394"
						y="161.186"
						width="31.5487"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="156.911"
						y="169.072"
						width="10.5162"
						height="10.5162"
						rx="2.62906"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<rect
						x="177.943"
						y="161.186"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="179.258"
						y="161.186"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="237.097"
						y="161.186"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="238.411"
						y="161.186"
						width="98.5897"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="337.001"
						y="161.186"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="338.316"
						y="161.186"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="346.203"
						y="169.072"
						width="42.0649"
						height="10.5162"
						rx="1.97179"
						fill={token('color.background.accent.blue.subtler', 'CCE0FF')}
					/>
					<rect
						x="396.155"
						y="161.186"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="397.469"
						y="161.186"
						width="26.2906"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="404.699"
						y="167.758"
						width="13.1453"
						height="13.1453"
						rx="6.57264"
						fill={token('color.background.accent.yellow.subtler.hovered', '#F5CD47')}
					/>
					<path
						d="M423.76 214.082H146.394V216.082H423.76V214.082Z"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="146.394"
						y="188.791"
						width="31.5487"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="156.911"
						y="196.678"
						width="10.5162"
						height="10.5162"
						rx="2.62906"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<rect
						x="177.943"
						y="188.791"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="179.258"
						y="188.791"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="237.097"
						y="188.791"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="238.411"
						y="188.791"
						width="98.5897"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="337.001"
						y="188.791"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="338.316"
						y="188.791"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="346.203"
						y="196.678"
						width="42.0649"
						height="10.5162"
						rx="1.97179"
						fill={token('color.background.accent.blue.subtler', 'CCE0FF')}
					/>
					<rect
						x="396.155"
						y="188.791"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="397.469"
						y="188.791"
						width="26.2906"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="404.7"
						y="195.364"
						width="13.1453"
						height="13.1453"
						rx="6.57264"
						fill={token('color.background.accent.red.subtle', '#F87168')}
					/>
					<path
						d="M423.76 241.687H146.394V243.687H423.76V241.687Z"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="146.394"
						y="216.396"
						width="31.5487"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="156.911"
						y="224.283"
						width="10.5162"
						height="10.5162"
						rx="2.62906"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<rect
						x="177.943"
						y="216.396"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="179.258"
						y="216.396"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="237.097"
						y="216.396"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="238.411"
						y="216.396"
						width="98.5897"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="337.001"
						y="216.396"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="338.316"
						y="216.396"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="346.203"
						y="224.283"
						width="42.0649"
						height="10.5162"
						rx="1.97179"
						fill={token('color.background.accent.green.subtler', '#BAF3DB')}
					/>
					<rect
						x="396.155"
						y="216.396"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="397.469"
						y="216.396"
						width="26.2906"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="404.699"
						y="222.97"
						width="13.1453"
						height="13.1453"
						rx="6.57264"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<path
						d="M423.76 269.292H146.394V271.292H423.76V269.292Z"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="146.394"
						y="243.999"
						width="31.5487"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="156.911"
						y="251.886"
						width="10.5162"
						height="10.5162"
						rx="2.62906"
						fill={token('color.icon.accent.blue', '#1D7AFC')}
					/>
					<rect
						x="177.943"
						y="244.001"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="179.258"
						y="243.999"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="237.097"
						y="244.001"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="238.412"
						y="243.999"
						width="98.5897"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="337.001"
						y="244.001"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="338.315"
						y="243.999"
						width="57.8393"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="346.202"
						y="251.886"
						width="42.0649"
						height="10.5162"
						rx="1.97179"
						fill={token('color.background.accent.green.subtler', '#BAF3DB')}
					/>
					<rect
						x="396.155"
						y="244.001"
						width="1.31453"
						height="26.2906"
						fill={token('color.border.disabled', '#091E42')}
					/>
					<rect
						x="397.469"
						y="243.999"
						width="26.2906"
						height="26.2906"
						fill={token('color.background.input', 'white')}
					/>
					<rect
						x="404.699"
						y="250.572"
						width="13.1453"
						height="13.1453"
						rx="6.57264"
						fill={token('color.background.accent.yellow.subtler.hovered', '#F5CD47')}
					/>
					<text
						fill={token('color.text.subtlest', '#626F86')}
						fontSize="6.84033"
						letterSpacing="0px"
						x="195.984"
						y="93.9126"
					>
						{transformProjectKeyText(projectKey, 1)}
					</text>
					<text
						fill={token('color.text.subtlest', '#626F86')}
						fontSize="6.84033"
						letterSpacing="0px"
						x="195.984"
						y="121.085"
					>
						{transformProjectKeyText(projectKey, 2)}
					</text>
					<text
						fill={token('color.text.subtlest', '#626F86')}
						fontSize="6.84033"
						letterSpacing="0px"
						x="195.984"
						y="149.662"
					>
						{transformProjectKeyText(projectKey, 3)}
					</text>
					<text
						fill={token('color.text.subtlest', '#626F86')}
						fontSize="6.84033"
						letterSpacing="0px"
						x="195.984"
						y="176.558"
					>
						{transformProjectKeyText(projectKey, 4)}
					</text>
					<text
						fill={token('color.text.subtlest', '#626F86')}
						fontSize="6.84033"
						letterSpacing="0px"
						x="195.984"
						y="205.135"
					>
						{transformProjectKeyText(projectKey, 5)}
					</text>
					<text
						fill={token('color.text.subtlest', '#626F86')}
						fontSize="6.84033"
						letterSpacing="0px"
						x="195.984"
						y="232.032"
					>
						{transformProjectKeyText(projectKey, 6)}
					</text>
					<text
						fill={token('color.text.subtlest', '#626F86')}
						fontSize="6.84033"
						letterSpacing="0px"
						x="195.984"
						y="259.768"
					>
						{transformProjectKeyText(projectKey, 7)}
					</text>
				</g>
				<defs>
					<filter
						id="filter0_d_4253_17324"
						x="13.7907"
						y="11.2639"
						width="426.582"
						height="285.378"
						filterUnits="userSpaceOnUse"
						colorInterpolationFilters="sRGB"
					>
						<feFlood floodOpacity="0" result="BackgroundImageFix" />
						<feColorMatrix
							in="SourceAlpha"
							type="matrix"
							values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
							result="hardAlpha"
						/>
						<feOffset dy="5.47227" />
						<feGaussianBlur stdDeviation="4.1042" />
						<feColorMatrix
							type="matrix"
							values={
								colorMode === 'dark'
									? '0 0 0 0 0.0117647 0 0 0 0 0.0156863 0 0 0 0 0.0156863 0 0 0 0.56 0'
									: '0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.16 0'
							}
						/>
						<feBlend
							mode="normal"
							in2="BackgroundImageFix"
							result="effect1_dropShadow_4253_17324"
						/>
						<feBlend
							mode="normal"
							in="SourceGraphic"
							in2="effect1_dropShadow_4253_17324"
							result="shape"
						/>
					</filter>
					<linearGradient
						id="paint0_linear_4253_17324"
						x1="74.9975"
						y1="-1.89206e-05"
						x2="228.889"
						y2="324.521"
						gradientUnits="userSpaceOnUse"
					>
						<stop offset="0.150366" stopColor="#0065FF" />
						<stop offset="1" stopColor="#D8F2F6" />
					</linearGradient>
				</defs>
			</svg>
		</IllustrationWrapper>
	);
};
